import React, { useState, useEffect } from 'react';
import { Settings } from '@mui/icons-material';
import { DynamicContainer } from './common/DynamicContainer';
import { toast } from 'react-toastify';
import GlobalAgentConfigDrawer from './AIAgentsDrawer/GlobalAgentConfigDrawer';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}asrosync/agents`;

const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
    };
};

const GlobalSchemaAdmin = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [agents, setAgents] = useState([]);
    const [agentDrawerOpen, setAgentDrawerOpen] = useState(false);
    const [selectedAgent, setSelectedAgent] = useState(null);
    useEffect(() => {
        fetchAgents();
    }, []);

    const fetchAgents = async () => {
        setIsLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/global`, {
                method: 'GET',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            const mappedData = data.map(item => ({
                ...item,
                id: item.agentId,
            }));
            setAgents(mappedData);
        } catch (error) {
            console.error('Error fetching global agents:', error);
            toast.error(`Error fetching agents: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreate = async (data) => {
        try {
            const cleanData = Object.fromEntries(
                Object.entries(data).filter(([_, v]) => v !== null && v !== '')
            );

            const response = await fetch(`${API_BASE_URL}/global`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify(cleanData)
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || `HTTP error! status: ${response.status}`);
            }

            toast.success('Global agent created successfully');
            await fetchAgents();
        } catch (error) {
            console.error('Create error:', error);
            toast.error(`Error creating agent: ${error.message}`);
            throw error;
        }
    };

    const handleUpdate = async (id, data) => {
        try {
            const cleanData = Object.fromEntries(
                Object.entries(data).filter(([_, v]) => v !== null && v !== '')
            );

            const response = await fetch(`${API_BASE_URL}/global/${id}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify(cleanData)
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || `HTTP error! status: ${response.status}`);
            }

            toast.success('Global agent updated successfully');
            await fetchAgents();
        } catch (error) {
            console.error('Update error:', error);
            toast.error(`Error updating agent: ${error.message}`);
            throw error;
        }
    };

    const handleDelete = async (id) => {
        try {
            const response = await fetch(`${API_BASE_URL}/global/${id}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            toast.success('Global agent deleted successfully');
            await fetchAgents();
        } catch (error) {
            console.error('Delete error:', error);
            toast.error(`Error deleting agent: ${error.message}`);
            throw error;
        }
    };

    const handleOpenAgentDrawer = (agent = null) => {
        setSelectedAgent(agent);
        setAgentDrawerOpen(true);
    };

    const handleAddClick = () => {
        handleOpenAgentDrawer(null);
    };

    const handleEditClick = (agent) => {
        handleOpenAgentDrawer(agent);
    };

    const handleCloseAgentDrawer = () => {
        setAgentDrawerOpen(false);
        setSelectedAgent(null);
    };

    const columns = [
        { field: 'agentName', headerName: 'Agent Name', width: 200 },
        { field: 'agentAppName', headerName: 'App Name', width: 120 },
        { field: 'category', headerName: 'Category', width: 150 },
        { field: 'baseUrl', headerName: 'Base URL', width: 200 },
        {
            field: 'webhookEndpoint',
            headerName: 'Webhook',
            width: 150,
            renderCell: (params) => (
                params.value ? 'Configured' : 'Not Set'
            )
        },
        {
            field: 'apiKey',
            headerName: 'API Key',
            width: 100,
            renderCell: (params) => (
                params.value ? 'Set' : 'Missing'
            )
        },
        { field: 'isActive', headerName: 'Active', width: 100, type: 'boolean' },
    ];

    const formFields = [
        {
            name: 'agentName',
            label: 'Agent Name',
            required: true,
            validate: (value) => {
                if (!value) return 'Agent name is required';
                if (value.length < 3) return 'Agent name must be at least 3 characters';
                return null;
            }
        },

        {
            name: 'agentAppName',
            label: 'Agent App Name',
            type: 'select',
            options: ['n8n', 'langchain'],
            required: true,
            helperText: 'Must match Agent platform on Global Tools (langchain / n8n)',
        },
        {
            name: 'category',
            label: 'Category',
            required: false
        },
        {
            name: 'description',
            label: 'Description',
            type: 'multiline',
            rows: 3,
            required: false
        },
        {
            name: 'baseUrl',
            label: 'Base URL',
            required: true,
            helperText: 'e.g., https://synctrack.asrotrack.com'
        },
        {
            name: 'webhookEndpoint',
            label: 'Webhook Endpoint',
            required: true,
            helperText: 'e.g., /n8n/production/abc123/chat'
        },
        {
            name: 'apiKey',
            label: 'API Key',
            type: 'password',
            required: false
        },
        {
            name: 'systemPrompt',
            label: 'System Prompt',
            type: 'multiline',
            rows: 4,
            required: false
        },
        {
            name: 'agentConfig',
            label: 'Agent Config (JSON)',
            type: 'multiline',
            rows: 6,
            required: false,
            helperText: 'Enter JSON configuration'
        },
        {
            name: 'isActive',
            label: 'Active',
            type: 'checkbox',
            required: false
        }
    ];

    const initialFormData = {
        agentName: '',
        agentAppName: '',
        category: '',
        description: '',
        baseUrl: '',
        webhookEndpoint: '',
        apiKey: '',
        systemPrompt: '',
        agentConfig: '',
        isActive: true
    };

    return (
        <>
            <DynamicContainer
                title="Global AI Agents (Admin)"
                icon={Settings}
                columns={columns}
                formFields={formFields}
                initialFormData={initialFormData}
                idField="id"
                rows={agents}
                onCreate={handleCreate}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                isLoading={isLoading}
                functionCode="AI Global Schema"
                onAddClick={handleAddClick}
                onEditClick={handleEditClick}
            />

            {/* Global Agent Configuration Drawer */}
            <GlobalAgentConfigDrawer
                open={agentDrawerOpen}
                onClose={handleCloseAgentDrawer}
                agent={selectedAgent}
                onSave={selectedAgent ?
                    (data) => handleUpdate(selectedAgent.agentId, data) :
                    handleCreate
                }
                onSuccess={fetchAgents}
            />
        </>
    );
};

export default GlobalSchemaAdmin;
